import { Component, effect, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserPsychologistService } from '../../services/user-psychologist.service';
import { UserPsychologist } from '../../models/user-psychologist';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatRipple } from '@angular/material/core';
import TYPES from '../../constants/types';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { Dialog } from '@angular/cdk/dialog';
import { ConfigService } from '../../services/config.service';
import { EdgeFunctionCallerService } from '../../services/edge-function-caller.service';
import { Psychologist } from '../../models/psychologists';
import { PsychologistService } from '../../services/psychologist.service';

@Component({
  selector: 'app-approve-user-connnection',
  imports: [
    MatIcon,
    TranslatePipe,
    MatRipple
  ],
  templateUrl: './approve-user-connnection.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './approve-user-connnection.component.scss'
})
export class ApproveUserConnnectionComponent {

  translate: TranslateService = inject(TranslateService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  configService: ConfigService = inject(ConfigService);
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);
  pendingUserPsychologists: WritableSignal<UserPsychologist[]> = signal<UserPsychologist[]>([]);
  
  dialog: Dialog = inject(Dialog);
  snackBar: MatSnackBar = inject(MatSnackBar);

  loading: WritableSignal<boolean> = signal<boolean>(false);

  constructor(){
    effect(()=>{
      if(this.psychologistService.psychologist()){
        this.loadingPendingRequests();
      }
    })
  }

  loadingPendingRequests(){
    this.loading.set(true);
    const psychologistId = this.psychologistService.psychologist()?.id;
    if(psychologistId){
      this.userPsychologistService.getAllPendingUsersPsychologists(psychologistId).then(
        (data) => {
          this.pendingUserPsychologists.set(data);
          this.loading.set(false);
        }
      );
    }else{
      this.loading.set(false);
    }
  }

  async approve(request: UserPsychologist){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    const requestId = request.id;
    if(requestId){

      this.loading.set(true);

      const { psychologists, users, ...updatedRequest } = request;      
      updatedRequest.status = TYPES.DATABASE.STATUS.ACTIVE.code;      
      await this.userPsychologistService.update(TYPES.DATABASE.TABLES.USERS_PSYCHOLOGISTS, requestId, updatedRequest);

      const language: string = this.configService.preferedLanguage() ? this.configService.preferedLanguage() : 'en'
      await this.edgeFunctionCallerService.sendNotification({
        to: request.users?.email || '',
        template: TYPES.APPLICATION.EMAILS_TYPES.CONNECTION_APPROVED,
        language: language,
        userName: request.users?.first_name || '',
        psychologistName: request.psychologists?.first_name || ''
      });

      const requestUserId = request.user_id;
      if(requestUserId){
        this.edgeFunctionCallerService.addPushNotification(
          requestUserId, 
          new Date().toISOString(),
          this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_APPROVED.title),
          this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_APPROVED.body),
          {
            onActionClick: {
              default: {operation: "navigateLastFocusedOrOpen", url: "/home"}
            }
          }
        );
      }

      this.loadingPendingRequests();

      const message:string = this.translate.instant('info.association_approved');
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
        
    }
  }

  async reject(request: UserPsychologist){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'reject-association-dialog.alert_title',
        message: 'reject-association-dialog.alert_message'
      },
    });

    dialogRef.closed.subscribe(async(result:any)  => {
      if(result?.action === 'continue'){
        
        this.loading.set(true);
        
        const requestId = request.id;
        if(requestId){
          await this.userPsychologistService.delete(TYPES.DATABASE.TABLES.USERS_PSYCHOLOGISTS, requestId);

          const language: string = this.configService.preferedLanguage() ? this.configService.preferedLanguage() : 'en'
          await this.edgeFunctionCallerService.sendNotification({
            to: request.users?.email || '',
            template: TYPES.APPLICATION.EMAILS_TYPES.CONNECTION_REJECTED,
            language: language,
            userName: request.users?.first_name || '',
            psychologistName: request.psychologists?.first_name || ''
          });

          const requestUserId = request.user_id;
          if(requestUserId){
            this.edgeFunctionCallerService.addPushNotification(
              requestUserId, 
              new Date().toISOString(),
              this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_REJECTED.title),
              this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_REJECTED.body),
              {
                onActionClick: {
                  default: {operation: "navigateLastFocusedOrOpen", url: "/home"}
                }
              }
            );
          }

          this.loadingPendingRequests();

          const message:string = this.translate.instant('info.association_rejected');
          const action:string = this.translate.instant('buttons.dismiss');
          this.snackBar.open(message, action);
        }        
      }
    });    
  }

}
